import React, { useRef } from "react";
import { Box, Button, Drawer, List, ListItemButton, ListItemText, AppBar, IconButton, Toolbar, Typography, CssBaseline } from "@mui/material";
import { Outlet } from "react-router";
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link, useLocation } from "react-router";
import { useTestProcedure } from "../../context/TestProcedureContext.tsx";
import { useTheme } from "@mui/material/styles";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useColorMode } from "../theme/ColorModeContextProvider.tsx";
import { useDecodeBinary } from "../../hooks/useDecodeBinary.ts";
import { useEncodeBinary } from "../../hooks/useEncodeBinary.ts";
import { useBinaryFileExtension } from "../../hooks/useBinaryFileExtension.ts"
import { useProject } from "../../context/ProjectContext.tsx";
const drawerWidth = 240;

const navItemsPreTestProcedures = [
  { label: "HowTo", path: "/" },
  { label: "Projekt", path: "/project"},
  { label: "Mechanik", path: "/mechanics" },
  { label: "Elektronik", path: "/electronics" },
  { label: "Prüfabläufe", path: "/testprocedure" },
];

const navItemsPostTestProcedures = [
  { label: "Zusammenfassung", path: "/summary"},
  { label: "Preis und Anfrage", path: "/price" },
];

const bottomNavItems = [
  { label: "Entwickleroptionen", path: "/developer" },
];


export const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { procedures } = useTestProcedure();
  const { toggleColorMode } = useColorMode();
  const theme = useTheme();
  const { encode } = useEncodeBinary();
  const { decode } = useDecodeBinary();
  const { fileExtension } = useBinaryFileExtension();
  const { project } = useProject();

  const handleExport = async () => {
    const blob = await encode()
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = new Date().toISOString().slice(0, 10).replace(/-/g, '') + "_" + project.name + fileExtension;
    link.click();
    URL.revokeObjectURL(url);
  };
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await decode(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ position: "relative" }}>
          <Box
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <Typography variant="h6" noWrap>
              qdTest Konfigurator
              {project?.name && (
                <Typography component="span" sx={{ ml: 6, fontWeight: "normal", opacity: 1 }}>
                  {project.name}
                </Typography>
              )}
            </Typography>
          </Box>
          <Box
            component="a"
            href="https://www.querdenkerengineering.de/qdtest-testsystem/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Querdenker Engineering GmbH"
              sx={{ height: 25 }}
            />
          </Box>
          <Box sx={{ marginLeft: "auto", display: "flex", gap: 2 }}>
            <Box sx={{ ml: 3, textAlign: "right", whiteSpace: "nowrap" }}>
              <div style={{ fontSize: "0.875rem", display: "flex", flexDirection: "column", gap: 0.3  }}>
                <a href="mailto:vertrieb@querdenkerengineering.de" style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                  <EmailIcon fontSize="small" />
                  vertrieb@querdenkerengineering.de 
                </a>
                <a href="tel:+4978078908010" style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                  <PhoneIcon fontSize="small" />
                  +49 (0)7807 890 80 10
                </a>
              </div>
            </Box>
            <Button variant="outlined" color="inherit" onClick={handleExport}>
              Export
            </Button>
            <Button variant="outlined" component="label" color="inherit">
              Import
              <input hidden type="file" accept={fileExtension} onChange={handleImport} ref={fileInputRef} />
            </Button>
            <IconButton onClick={toggleColorMode} color="inherit">
              {theme.palette.mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: "border-box" },
        }}
      >
        <Toolbar />
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <List>
            {navItemsPreTestProcedures.map((item) => (
              <ListItemButton
                key={item.label}
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
            {procedures.map((proc) => {
              const normalizedPath = location.pathname.replace(/\/$/, "");
              const isSelected = normalizedPath === `/testprocedure/${proc.id}`;
              return (
                <ListItemButton
                  key={proc.id}
                  component={Link}
                  to={`/testprocedure/${proc.id}`}
                  selected={isSelected}
                  sx={{ pl: 4 }}
                >
                  <ListItemText primary={proc.name || "Unbenannt"} />
                </ListItemButton>
              );
            })}
            {navItemsPostTestProcedures.map((item) => (
              <ListItemButton
                key={item.label}
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ mt: "auto" }}>
            <List>
              {bottomNavItems.map((item) => (
                <ListItemButton
                  key={item.label}
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
